import { SelectChangeEvent, Stack } from "@mui/material"
import {
    Button,
    Chip,
    DialogHandler,
    Dropdown,
    EEditorType,
    EnhancedFormModel,
    EPopupBranding,
    EPopupPosition,
    EPopupTriggerType,
    fetchRoute,
    FormModel,
    FormsFactory,
    FullBar,
    GenericWrapperContext,
    getBrandUrl,
    Icon,
    initFieldValidation,
    InlineTextEdit,
    ListModel,
    ListsFactory,
    LoadingContainer,
    Modal,
    PopupModel,
    PopupsFactory,
    ResourceEdit,
    TextField,
    TFieldValidation,
    TPopupModel,
    TPopupTrigger,
    Typography
} from "@cakemail-org/ui-components-v2"
import { Trans, useTranslation } from "react-i18next"
import { useContext, useState, useEffect, useMemo } from "react"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { useLocation, useParams } from "react-router-dom"
import BeeEditor from "../../../components/beeEditor"
import { ResourcePreview } from "../../../components/Resources/preview"
import "../styles.scss"
import { ResourceCopy } from "../../../components/Resources/copy"
import { AddTagsButton } from "../../../components/Tags"
import { TEditPopup } from "../types"
import { getPositionStyle } from "../utils"
import { logError } from "../../../utils/data"
import { ELogErrorMsg } from "../../../utils/data/types"
import { hasFeatureFlag } from "../../../utils/limits_features"
import { PopupSampleQueryCondition } from "../../../components/filtering/queryComponents/condition/popupSample"
import { OptInPolicy } from "../../../components/forms/optInPolicy/OpinPolicy"
import { FromCommonResourceEdit } from "../../../components/forms/commonResource"
import { buildDefaultUrls, updateFormLayoutOptions } from "../../forms/utils"


export default function PopupDetails({ }: TEditPopup) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { popupId } = useParams()

    const location = useLocation()

    const [isEditing, setIsEditing] = useState<boolean>(location.pathname.split('/').pop() === 'edit')

    const [popup, setPopup] = useState<PopupModel | undefined>(undefined)
    const [form, setForm] = useState<EnhancedFormModel | undefined>(undefined)

    const [formFieldStates, setFormFieldStates] = useState<Record<string, TFieldValidation>>({
        ...initFieldValidation("post_redirect_url", { value: form?.post_redirect_url }),
        ...initFieldValidation("double_opt_in_redirect_url", { value: form?.double_opt_in_redirect_url })
    })

    const [selectedList, setSelectedList] = useState<ListModel>()
    const [previewContent, setPreviewContent] = useState<string | undefined>(undefined)

    useEffect(() => {
        fetchPopup().then((response) => {
            if (response?.list_id) {
                ListsFactory.get({ id: response.list_id })
                    .then((list) => {
                        setSelectedList(list)
                    })
            }
        })
    }, [popupId])

    async function fetchPopup() {
        return PopupsFactory.get({ id: popupId || "" })
            .then(async (popup) => {
                if (popup?.form_id) {
                    await fetchForm(popup.form_id).then(async (form) => {
                        popup.content.json = await updateFormLayoutOptions({
                            content: popup.content.json,
                            listId: popup.list_id,
                            submissionUrl: form.submission_url
                        })
                    })
                }

                setPopup(popup)
                renderPopup(popup)
                return popup
            }).catch(() => {
                DialogHandler.show({
                    type: "warning",
                    title: t("general-error-msg")
                })
                window.location.href = "#" + fetchRoute({ route: ERoutes.popups })
            })
    }

    async function fetchForm(id: string): Promise<EnhancedFormModel> {
        return FormsFactory.get({ id: id })
            .then((form) => {
                setForm(form as EnhancedFormModel)
                return form as EnhancedFormModel
            })
    }

    async function renderPopup(cPopup?: PopupModel) {
        const popupToRender = cPopup || popup
        if (popupToRender) {
            popupToRender.render().then((content) => {
                setPreviewContent(getPositionStyle(popupToRender.content.position) + content)
            }).catch(() => {
                setPreviewContent("")
            })
        }
    }

    function handlePublish(e: React.MouseEvent<HTMLButtonElement>, btn: TButtonRef) {
        hasFeatureFlag
        if (popup) {
            popup.publish().then((popup) => {
                DialogHandler.show({
                    type: "success",
                    title: t("general-published")
                })
                setPopup(popup)
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorPublishingPopup, error)
            }).finally(() => {
                btn.reset()
            })
        }
    }

    async function handleBrandingToggle(): Promise<any> {
        if (popup) {
            return savePopup({
                dataToUpdate: {
                    branding: popup.branding === EPopupBranding.smart ? EPopupBranding.basic : EPopupBranding.smart
                },
                render: true
            }).then((data) => {
                if (data) {
                    return data?.content?.json || null
                }
            })
        }
    }


    function handleInlineFieldConfirm({ name, value }: { name?: string, value?: string }) {
        if (popup) {
            savePopup({ dataToUpdate: { [name as "name"]: value || "" } })
        }
    }


    function handleTag(tag: string, btn: TButtonRef) {
        if (popup) {
            savePopup({ dataToUpdate: { tags: [...(popup.tags || []), tag] } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleUntag(tag: string, btn: TButtonRef) {
        if (popup) {
            savePopup({ dataToUpdate: { tags: (popup.tags || []).filter((t) => t !== tag) } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleDDChanges(event: SelectChangeEvent<string>) {
        const { name, value } = event.target

        if (!popup) return

        const path = name.split('.')
        const dataToUpdate: Partial<TPopupModel> = {}

        if (path.length > 1) {
            const current = popup
            const lastKey = path.pop() || ''
            let target = dataToUpdate

            // Build nested structure preserving existing data
            path.forEach((pathKey, index) => {
                const key = pathKey as keyof TPopupModel
                if (index === 0) {
                    dataToUpdate[key] = current?.[key as keyof TPopupModel] || {} as any
                    target = dataToUpdate[key] as Partial<TPopupModel>
                } else {
                    target[key] = { ...((current?.[path[0] as keyof TPopupModel] as any)?.[key]) }
                    target = target[key] as Partial<TPopupModel>
                }
            })

            target[lastKey as keyof TPopupModel] = value as any
        } else {
            dataToUpdate[name as keyof TPopupModel] = value as any
        }
        setPopup(new PopupModel({ ...popup, ...dataToUpdate }))
        savePopup({ dataToUpdate, render: name === "content.position" })
    }


    async function handleOnEditorSave(jsonFile: string, htmlFile: string): Promise<any> {
        if (popup) {
            return savePopup({
                dataToUpdate: {
                    content: {
                        position: popup.content.position,
                        json: JSON.parse(jsonFile)
                    }
                },
                render: true
            })
        }
    }

    function handleOnClose() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.popups })
    }

    function handlePopupEdit() {
        setIsEditing(true)
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupEdit, params: { popupId: popupId } })
    }

    function handleOnEditorClose() {
        setIsEditing(false)
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupDetails, params: { popupId: popupId } })
    }

    async function savePopup({ dataToUpdate, render }: { dataToUpdate?: Partial<TPopupModel>, render?: boolean } = {}) {
        const popupToSave = dataToUpdate || popup
        if (popupToSave) {
            return popup?.save(popupToSave).then((data) => {
                if (render) {
                    renderPopup()
                }
                setPopup(data)
                return data
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorSavingPopup, error)
            })
        }
    }

    async function saveForm(dataToUpdate?: Partial<EnhancedFormModel | void>) {
        const formToSave = dataToUpdate || form
        if (formToSave) {
            return form?.save(formToSave).then((response) => {
                setForm(new EnhancedFormModel(response))
                return response
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorSavingForm, error)
            })
        }
    }

    function handleTriggerValueChange({ name, value }: { name: string, value: any }) {
        if (popup) {
            popup.trigger[name as keyof TPopupTrigger] = value as never
            setPopup(new PopupModel(popup))
            savePopup({ dataToUpdate: { trigger: { ...popup.trigger } } })
        }
    }

    function handleTriggerConditionChange(condition: EPopupTriggerType) {
        if (popup) {
            popup.trigger.type = condition
            setPopup(new PopupModel(popup))
            savePopup({ dataToUpdate: { trigger: { ...popup.trigger, type: condition } } })
        }
    }

    const isPublished = popup?.published_on && popup.published_on > 0
    const hasUnpublishedChanges = popup && ((popup?.published_on && popup.published_on > 0 && popup?.updated_on - 1 > popup?.published_on) || popup?.published_on == null)

    const layoutOptions = useMemo(() => [
        { text: t("general-center"), value: EPopupPosition.center },
        { text: t("general-top-right"), value: EPopupPosition.top_right },
        { text: t("general-top-left"), value: EPopupPosition.top_left },
        { text: t("general-bottom-left"), value: EPopupPosition.bottom_left },
        { text: t("general-bottom-right"), value: EPopupPosition.bottom_right },
        { text: t("general-drawer-left"), value: EPopupPosition.drawer_left },
        { text: t("general-drawer-right"), value: EPopupPosition.drawer_right },
        { text: t("general-drawer-top"), value: EPopupPosition.drawer_top },
        { text: t("general-drawer-bottom"), value: EPopupPosition.drawer_bottom },
    ], [])

    const triggerTypeDataMap = useMemo(() => {
        return {
            [EPopupTriggerType.on_page_load]: null,
            [EPopupTriggerType.on_time_delay]: "delay_seconds",
            [EPopupTriggerType.on_click]: "selector",
            [EPopupTriggerType.on_scroll]: "scroll_percentage",
            [EPopupTriggerType.on_exit_intent]: null
        }
    }, [])

    return <Modal className={"editPopupModal"}
        open
        heading={{
            logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
            titleContent: [
                <Typography key="popupTitle" variant="title" color="body1.main">{t("general-popup_one")}</Typography>,
                ...(popup && [<Chip key="popupChip" label={t(isPublished ? "general-published" : "general-unpublished")} color={isPublished ? "secondary" : "primary"} />] || [])
            ],
            actions: [
                <Button key="publish" animated disabled={!popup} color="secondary" variant='contained' startIcon={<Icon name={hasUnpublishedChanges ? "Publish18" : "Checkmark14"} />} onClick={handlePublish} >
                    {t(hasUnpublishedChanges ? "general-publish" : "general-published")}
                </Button>
            ],
            closeBtnText: t("general-close"),
            closeBtnOnClick: handleOnClose
        }}
        content={
            <LoadingContainer loading={!popup}>
                {popup && <Stack className="editPopupContainer" direction="row">
                    <Stack className="editPopupDataContainer" gap={6}>
                        <FullBar
                            left={
                                <Typography variant="title" color="primary.main">
                                    <InlineTextEdit
                                        name="name"
                                        tooltipTitle={t("popups-details-name-tooltip")}
                                        onConfirm={handleInlineFieldConfirm}
                                        defaultValue={popup.name || ""}
                                        placeholder={t("general-untitled")}
                                    />
                                </Typography>

                            }
                            right={[
                                <Typography key="tagCount" variant="body1" className="semiBold">
                                    {popup.tags?.length || 0} {t("general-tag", { count: popup.tags?.length || 0 })}
                                </Typography>,
                                <AddTagsButton key="addTagsButton" tagList={popup.tags || []} onTag={handleTag} onUntag={handleUntag} />
                            ]}
                        />

                        <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("popups-details-settings-title")}
                            </Typography>

                            <ResourceEdit
                                title={t("general-audience_one")}
                                desc={t("form-audience-desc")}
                                support={<Typography variant="body1">{t("form-audience-support")} <b>'{selectedList?.name || ""}'</b></Typography>}
                            />


                            <ResourceEdit
                                status="valid"
                                title={t("popups-details-layout-title")}
                                desc={t("popup-details-layout-desc")}
                                support={<Typography variant="body1">
                                    <Trans
                                        i18nKey={
                                            [
                                                EPopupPosition.drawer_left,
                                                EPopupPosition.drawer_right,
                                                EPopupPosition.drawer_top,
                                                EPopupPosition.drawer_bottom
                                            ].includes(popup.content.position) ? "popups-details-layout-support-drawer"
                                                : "popups-details-layout-support"
                                        }
                                        values={{
                                            layout: layoutOptions.find(
                                                (option) => option.value === popup.content.position
                                            )?.text
                                        }} />
                                </Typography>}
                                editable
                                editableContent={<Dropdown
                                    name="content.position"
                                    options={layoutOptions}
                                    value={popup.content.position}
                                    onChange={handleDDChanges}
                                />}
                            />

                            {/*<ResourceEdit
                            editable
                            status="valid"
                            title={t("general-targeting")}
                            desc={t("popup-details-targeting-desc")}
                            support={"Show popup to X targeting options"}
                            editableContent={"Editable content"}
                        />*/}
                            <ResourceEdit
                                editable
                                status="valid"
                                title={t("general-trigger_one")}
                                desc={t("popup-details-triggers-desc")}
                                support={<Typography variant="body1">
                                    <Trans
                                        i18nKey={"popup-details-triggers-support-" + popup.trigger.type}
                                        values={{ value: popup.trigger[triggerTypeDataMap[popup.trigger.type] as keyof TPopupTrigger] }}
                                    />
                                </Typography>}
                                editableContent={<Stack className="queryConditionContainer" gap={4}>
                                    <Typography variant="body1L" className="semiBold">
                                        {t("query-builder-trigger-group-title")}
                                    </Typography>

                                    <PopupSampleQueryCondition
                                        key={popup.trigger.type}
                                        onValueChange={handleTriggerValueChange}
                                        defaultValue={popup.trigger[triggerTypeDataMap[popup.trigger.type] as keyof TPopupTrigger]}
                                        condition={popup.trigger.type}
                                        onConditionChange={handleTriggerConditionChange}
                                    />
                                </Stack>}
                            />

                            <ResourceEdit
                                title={t("general-embed-code")}
                                desc={t("popup-embed-snippet-desc")}
                                support={popup?.published_on && popup.published_on > 0 ?
                                    <ResourceCopy
                                        isCodeBlock
                                        text={popup?.content.published_content || ""} />
                                    :
                                    <Typography>{t("popup-embed-snippet-not-published")}</Typography>
                                }
                            />
                        </Stack>

                        {form && <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("forms-details-settings-title")}
                            </Typography>


                            <FromCommonResourceEdit
                                form={form}
                                saveForm={saveForm}
                            />

                        </Stack>}

                    </Stack>



                    <ResourcePreview
                        content={previewContent}
                        actions={[
                            <Button
                                key="edit"
                                color="primary"
                                variant='contained'
                                size="small"
                                onClick={handlePopupEdit} >
                                {t("general-edit-popup")}
                            </Button>
                        ]}
                    />

                    <BeeEditor
                        key={popup?.content.position}
                        hidden={!isEditing}
                        titleContent={<Typography variant="title" >{t("general-edit-popup")}</Typography>}
                        onClose={handleOnEditorClose}
                        editorType={EEditorType.popUpDesigner}
                        initContent={popup?.content?.json}
                        onSave={handleOnEditorSave}
                        onBrandingToggle={handleBrandingToggle}
                        contentContext={{
                            popupPosition: popup?.content.position,
                            list: selectedList,
                            formAction: form?.submission_url,
                            isSmart: popup?.branding === "smart"
                        }}
                    />


                </Stack>
                }
            </ LoadingContainer>
        }
    />
}